import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";
import SalonService from "../../services/SalonService";
import "react-calendar/dist/Calendar.css";
import moment from "moment";
import "./ViewBookings.css";
import { toast } from "react-toastify";
import { useSalon } from "../../context/SalonContext";
import GlobalLoader from "../loadingIndicator/GlobalLoader";
import "../../../src/style.css";


const ViewBookings = () => {
    const { customerId } = useSalon();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [dataSource, setDataSource] = useState([]);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    var today = moment().format('YYYY-MM-DD');
    const [cancellationReason, setCancellationReason] = useState("");

    useEffect(() => {
        const timeout = setTimeout(() => setLoading(false), 30000);
        fetchAppointments(0);

        return () => clearTimeout(timeout);
    }, []);


    const fetchAppointments = async (page = 0) => {
        const year = new Date().getFullYear();
        setLoading(true);

        try {
            const response = await SalonService.getUserAppointments(customerId, year, page);
            const content = response?.data?.content || [];

            if (page === 0) {
                setDataSource(content);
            } else {
                setDataSource(prev => [...prev, ...content]);
            }

            if (content.length === 0) {
                setHasMore(false); // 👈 stop loading
            }
        } catch (error) {
            console.error("Error fetching appointments:", error);
        } finally {
            setLoading(false);
        }
    };

    const loadMore = () => {
        if (loading || !hasMore) return;

        const nextPage = offset + 1;
        setOffset(nextPage);
        fetchAppointments(nextPage);
    };

    const cancellationAllowed = (apntDetails) => {
        var today = moment().format('YYYY-MM-DD');
        const currentDate = new Date();
        let timeInMinutes = (currentDate.getHours() * 60) + currentDate.getMinutes();

        return (apntDetails.apntStatus === 'BOOKED' && ((apntDetails.apntDate > today) || (apntDetails.apntDate === today
            && timeInMinutes < apntDetails.apntStart - 60)));
    }

    const cancelAppointment = async (apntId, reason) => {
        setLoading(true);
        try {
            const response = await SalonService.cancelAppointment({ apntId, reason });
            if (response.data) {
                toast.success('Appointment cancelled successfully');
            } else {
                toast.error('Appointment cancellation failed, try again or contact salon')
            }
        } catch (error) {
            toast.error('Appointment cancellation failed, try again or contact salon');
        } finally {
            setLoading(false);
            navigate('/home', { replace: true });
        }
    };

    const PaymentInfoRow = ({ item }) => {
        if (item.paymentStatus === "NOT_REQUIRED") return null;
        if (!item.advanceAmount) return null;

        const isPaid = ["PAID", "VERIFIED"].includes(item.paymentStatus);
        const isSubmitted = item.paymentStatus === "SUBMITTED";

        return (
            <div className="row payment-info-row">
                <span className="label">Advance:</span>

                <span
                    className={`payment-info-value ${isPaid ? "paid" : isSubmitted ? "pending" : ""
                        }`}
                >
                    {isPaid
                        ? `Paid ₹${item.advanceAmount}`
                        : `₹${item.advanceAmount}`}
                </span>

                {/* ⓘ Refund policy tooltip */}
                {item.refundPolicyText && (
                    <span
                        className="refund-tooltip"
                        title={item.refundPolicyText}
                    >
                        ⓘ
                    </span>
                )}
            </div>
        );
    };

    const PaymentBadge = ({ item }) => {
        if (item.apntStatus !== "BOOKED") return null;

        if (item.paymentStatus === "PENDING") {
            return (
                <button
                    className="pay-now-btn"
                    onClick={() =>
                        navigate(`/pay/${item.bookingRef}`, {
                            state: {
                                customerId,
                                salonUuid: item.salonUuid,
                                bookingRef: item.bookingRef,
                                amount: item.advanceAmount,
                            },
                        })
                    }
                >
                    💳 Pay ₹{item.advanceAmount}
                </button>
            );
        }

        if (item.paymentStatus === "SUBMITTED") {
            return (
                <button
                    className="pay-now-btn disabled"
                    disabled
                >
                    ⏳ Verification Pending
                </button>
            );
        }

        if (["PAID", "VERIFIED"].includes(item.paymentStatus)) {
            return (
                <span className="payment-badge success">
                    Paid ₹{item.advanceAmount}
                </span>
            );
        }

        if (item.paymentStatus === "NOT_REQUIRED") {
            return <span className="payment-badge info">Pay at Salon</span>;
        }

        return null;
    };

    return (
        <div className="app-container">

            <div className="app-sub-container">

                <h2 className="header-title">My Appointments</h2>

                {loading && <GlobalLoader />}

                {!loading && dataSource.length === 0 && (
                    <p className="no-appointments">No appointments found.</p>
                )}

                {!loading && dataSource.map((item) => (
                    <div
                        key={item.apntId}
                        className={`appointment-card ${today > item.apntDate ? "past" : "upcoming"}`}
                        onClick={() => navigate(`/appointment/${item.apntId}`, {
                            state: {
                                apntId: item.apntId,
                                salonUuid: item.salonUuid
                            }
                        })}
                    >
                        {item.image && (
                            <img
                                src={`data:image/jpeg;base64,${item.image}`}
                                className="appointment-image"
                                alt="Salon"
                            />
                        )}

                        <div className="appointment-details">
                            <div className="appointment-header">
                                <h3 className="salon-name">{item.salonName}</h3>
                                <span
                                    className={`status-badge ${item.apntStatus.toLowerCase()}`}
                                >
                                    {item.apntStatus}
                                </span>
                            </div>

                            {/* ✅ NEW: Always show advance amount when applicable */}
                            <PaymentInfoRow item={item} />
                            <PaymentBadge item={item} />

                            <div className="row inline">
                                <span className="label">Date:</span>
                                <span className="value">{moment(item.apntDate).format("DD/MM/YYYY")}</span>
                            </div>

                            <div className="row inline">
                                <span className="label">Time:</span>
                                <span className="value">{item.apntTime}</span>
                            </div>

                            <div className="row">
                                <span className="label">Booked Services:</span>
                                <span className="value">{item.serviceNames}</span>
                            </div>

                            {item.apntDate >= today && item.apntStatus === "BOOKED" && (
                                <div className="row">
                                    <span className="label">Verification No:</span>
                                    <span className="value">{item.verificationNumber !== "null" ? item.verificationNumber : ""}</span>
                                </div>
                            )}

                            {cancellationAllowed(item) && (
                                <div className="cancel-section">
                                    <textarea
                                        className="cancel-textarea"
                                        placeholder="Enter cancellation reason..."
                                        value={cancellationReason}
                                        onChange={(e) => setCancellationReason(e.target.value)}
                                    />
                                    <button
                                        onClick={() => cancelAppointment(item.apntId, cancellationReason)}
                                        className="cancel-button"
                                    >
                                        Cancel Appointment
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                ))}

                {!loading && hasMore && dataSource.length > 0 && (
                    <div className="load-more-container">
                        <button
                            className="load-more-btn"
                            onClick={loadMore}
                        >
                            Load More
                        </button>
                    </div>
                )}

            </div>
        </div>
    );

};

export default ViewBookings;
